import { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  FolderKanban,
  History,
  MessageSquareText,
  ClipboardList,
  BookText,
  Library,
  BarChart3,
  Settings,
} from "lucide-react";
import { SHELL, NAV } from "@/constants/testIds";
import { getSignatureSurgeon } from "@/data/viewModels";
import { useWorkspaceMode } from "@/context/WorkspaceModeContext";
import ConversationsSidebar, {
  CONVERSATIONS,
} from "@/components/system/ConversationsSidebar";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Dashboard", Icon: LayoutDashboard, testId: NAV.dashboard },
  { to: "/cases", label: "Cases", Icon: FolderKanban, testId: NAV.cases },
  { to: "/timeline", label: "Surgical Memory Timeline", Icon: History, testId: NAV.timeline },
  { to: "/ask", label: "Ask Ni²", Icon: MessageSquareText, testId: NAV.ask },
  { to: "/pre-case", label: "Pre-Case Review", Icon: ClipboardList, testId: NAV.precase },
  { to: "/mm", label: "M&M Builder", Icon: BookText, testId: NAV.mm },
  { to: "/library", label: "Knowledge Library", Icon: Library, testId: NAV.library },
  { to: "/analytics", label: "Analytics", Icon: BarChart3, testId: NAV.analytics },
  { to: "/settings", label: "Settings", Icon: Settings, testId: NAV.settings },
];

export default function Sidebar() {
  const { mode, activeConversationId, setActiveConversationId } = useWorkspaceMode();
  const SURGEON = getSignatureSurgeon();

  if (mode === "ni2") {
    return (
      <div data-testid={SHELL.sidebar}>
        <ConversationsSidebar
          activeId={activeConversationId}
          onSelect={setActiveConversationId}
          onNew={() => setActiveConversationId(null)}
        />
      </div>
    );
  }

  return (
    <aside
      data-testid={SHELL.sidebar}
      className="nn-glass-sidebar flex h-full w-[224px] shrink-0 flex-col p-4"
    >
      {/* Surgeon profile */}
      <div className="mb-4 flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="relative">
          <div className="grid h-11 w-11 place-items-center rounded-full bg-gradient-to-br from-[#5b86c7] to-[#213d69] text-[13px] font-semibold text-white/90 shadow-inner shadow-white/10">
            MR
          </div>
          <span className="absolute -right-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full bg-[hsl(var(--nn-ok))] ring-2 ring-[#0e1524]" />
        </div>
        <div className="min-w-0 leading-tight">
          <div className="truncate text-[13.5px] font-semibold text-[hsl(var(--nn-ink))]">
            {SURGEON.name}
          </div>
          <div className="truncate text-[11px] text-[hsl(var(--nn-ink-muted))]">
            {SURGEON.specialty}
          </div>
          <div className="truncate text-[10.5px] text-[hsl(var(--nn-ink-faint))]">
            {SURGEON.institution}
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex flex-col gap-0.5">
        <div className="mb-1 mt-2 px-2 text-[10px] font-medium uppercase tracking-[0.18em] text-[hsl(var(--nn-ink-faint))]">
          Workspace
        </div>
        {NAV_ITEMS.map(({ to, label, Icon, testId }) => (
          <NavLink key={to} to={to} data-testid={testId}>
            {({ isActive }) => (
              <span className="nn-nav-item" data-active={isActive || undefined}>
                <Icon
                  className="h-3.5 w-3.5 shrink-0"
                  strokeWidth={1.6}
                  style={{ opacity: isActive ? 1 : 0.75 }}
                />
                <span className="truncate">{label}</span>
              </span>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Sync status */}
      <div className="mt-auto pt-4">
        <div className="nn-divider mb-3" />
        <div
          data-testid={SHELL.syncBadge + "-sidebar"}
          className="flex items-center gap-2 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 py-2.5"
        >
          <span className="relative flex h-2 w-2 items-center justify-center">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--nn-ok))] opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[hsl(var(--nn-ok))]" />
          </span>
          <div className="min-w-0 leading-tight">
            <div className="text-[11.5px] font-medium text-[hsl(var(--nn-ink))]">
              Memory synced
            </div>
            <div className="text-[10.5px] text-[hsl(var(--nn-ink-faint))] font-mono">
              Last indexed 2m ago
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
